import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CompleteUser } from '../../types';

const schema = yup.object().shape({
  reason: yup.string().optional(),
});

type FormData = {
  reason?: string;
};

interface BlockUnblockModalProps {
  user: CompleteUser | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  action: 'block' | 'unblock';
  loading: boolean;
}

const BlockUnblockModal: React.FC<BlockUnblockModalProps> = ({
  user,
  open,
  onClose,
  onConfirm,
  action,
  loading,
}) => {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      reason: '',
    },
  });

  React.useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = (data: FormData) => {
    onConfirm(data.reason);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6">
          {action === 'block' ? 'Block User' : 'Unblock User'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to {action} user <strong>{user.username}</strong>?
          </Alert>
          
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={`Reason for ${action}ing (optional)`}
                multiline
                rows={3}
                placeholder={`Enter reason for ${action}ing this user...`}
                error={!!errors.reason}
                helperText={errors.reason?.message}
                sx={{ mt: 2 }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color={action === 'block' ? 'error' : 'success'}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? `${action === 'block' ? 'Blocking' : 'Unblocking'}...` : action === 'block' ? 'Block User' : 'Unblock User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlockUnblockModal; 